import React, { useId, type ReactNode } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import Svg, { Circle, Defs, RadialGradient, Stop } from 'react-native-svg';
import { colors } from '../theme/tokens';

export interface FondNuitProps {
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

const TAILLE_HAUT = 240;
const TAILLE_BAS = 200;

/**
 * Fond bleu nuit avec deux halos flous, un violet en haut à droite et un cyan en bas à gauche. C'est
 * le fond des cartes de score et des en-têtes sombres de la refonte. Même technique que la page
 * Carrière : un dégradé radial SVG qui s'efface vers ses bords, jamais un cercle à contour net.
 *
 * Les halos sont décoratifs : masqués au lecteur d'écran, jamais touchables, découpés au cadre de la
 * carte. Ils sont immobiles ; l'animation de la maquette viendra avec l'option « réduire les
 * animations » qu'elle exige, pas avant.
 */
export function FondNuit({ children, style, testID }: FondNuitProps) {
  // Un identifiant par instance : deux cartes à l'écran ne doivent pas se partager un dégradé.
  const base = useId().replace(/:/g, '');
  return (
    <View testID={testID} style={[styles.fond, style]}>
      <View
        testID={testID ? `${testID}-halos` : undefined}
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={StyleSheet.absoluteFill}
      >
        <Halo id={`${base}-v`} couleur={colors.violetLogo} opacite={0.55} taille={TAILLE_HAUT} style={styles.haut} />
        <Halo id={`${base}-c`} couleur={colors.cyan} opacite={0.45} taille={TAILLE_BAS} style={styles.bas} />
      </View>
      {children}
    </View>
  );
}

function Halo({
  id,
  couleur,
  opacite,
  taille,
  style,
}: {
  id: string;
  couleur: string;
  opacite: number;
  taille: number;
  style: ViewStyle;
}) {
  return (
    <Svg width={taille} height={taille} style={[styles.halo, style]}>
      <Defs>
        <RadialGradient id={id} cx="50%" cy="50%" r="50%">
          <Stop offset="0" stopColor={couleur} stopOpacity={opacite} />
          <Stop offset="1" stopColor={couleur} stopOpacity={0} />
        </RadialGradient>
      </Defs>
      <Circle cx={taille / 2} cy={taille / 2} r={taille / 2} fill={`url(#${id})`} />
    </Svg>
  );
}

const styles = StyleSheet.create({
  fond: { backgroundColor: colors.night, overflow: 'hidden' },
  halo: { position: 'absolute' },
  haut: { top: -TAILLE_HAUT * 0.42, right: -TAILLE_HAUT * 0.38 },
  bas: { bottom: -TAILLE_BAS * 0.55, left: -TAILLE_BAS * 0.4 },
});
